import { isLoggedInAsObserver } from 'auth/utils';
import { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'react-query';
import { getPartyId } from 'utils/partyId/setPartyId';
import { http } from '../http';
import type {
  NotificationDateAccessed,
  NotificationResponse,
  Notification,
} from '../__generated__/api';

async function getNotifications(): Promise<Notification[]> {
  const response = await http.get<NotificationResponse>('/api/notifications');
  return response.data.notifications;
}

async function getNotificationDocument(reference: string) {
  const response = await http.get(`/api/notifications/${reference}/document`, {
    responseType: 'arraybuffer',
  });
  return response.data;
}

async function updateNotification(reference: string): Promise<void> {
  const notificationDateAccessed: NotificationDateAccessed = {
    dateAccessed: new Date().toISOString(),
  };
  await http.patch(`/api/notifications/${reference}`, notificationDateAccessed);
}

export function useQueryNotifications() {
  const isObserver: boolean = isLoggedInAsObserver();
  let isCallEnabled: boolean = true;

  if (isObserver) {
    const partyId: string | null = getPartyId();
    isCallEnabled = !!partyId;
  }

  return useQuery(['notifications'], () => getNotifications(), {
    enabled: isCallEnabled,
    staleTime: Infinity,
  });
}

export function useQueryNotificationDocument(reference: string) {
  const { status, data, isLoading } = useQuery(
    ['document', reference],
    () => getNotificationDocument(reference),
    {
      enabled: !!reference,
    }
  );

  return {
    status,
    data,
    isLoading,
  };
}

export function useMutationNotification() {
  return useMutation((reference: string) => updateNotification(reference), {});
}

export function useNotificationCount() {
  const [notificationCount, setCount] = useState(0);
  const notifications = useQueryNotifications();
  useEffect(() => {
    setCount(notifications?.data?.filter((item) => !item.dateAccessed).length ?? 0);
  }, [notifications]);
  return { notificationCount };
}
